import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const Header = ({ config }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!config) config = {};

  const navItems = [
    { label: 'Home', url: '/' },
    { 
      label: 'Accountability', 
      url: '/accountability',
      submenu: [
        { label: 'Trump EO', url: '/trump-executive-order' },
        { label: 'List Maintenance', url: '/list-maintenance' },
        { label: 'Board Compliance', url: '/board-compliance' }
      ]
    },
    { 
      label: 'Legislation', 
      url: '/legislation',
      submenu: [
        { label: 'Voter ID', url: '/voter-id' },
        { label: 'Signature Verification', url: '/signature-verification' }
      ]
    },
    { 
      label: 'Citizen Action', 
      url: '/citizen-action',
      submenu: [
        { label: 'Sign the Petition', url: '/sign-the-petition' },
        { label: "Poll Watcher's Toolkit", url: '/blog/poll-watchers-toolkit' },
        { label: "Canvass Watcher's Toolkit", url: '/canvass-watchers-toolkit' },
        { label: 'Testify at MGA', url: '/blog/testify-at-mga' }
      ]
    },
    { 
      label: 'Resources', 
      url: '/resources',
      submenu: [
        { label: 'Spotlight Series', url: '/spotlight-series' },
        { label: 'Data Source Statement', url: '/data-source-statement' },
        { label: 'Check Voter Registration', url: '/check-voter-registration' },
        { label: 'Be an Election Judge', url: '/be-an-election-judge' },
        { label: 'Board of Elections Directory', url: '/board-of-elections-directory' },
        { label: 'Find Your Representatives', url: 'http://www.marylandiq.org', external: true },
        { label: 'Maryland NVRA Violations', url: '/maryland-nvra-violations' },
        { label: 'Litigation Documents', url: '/litigation-documents' }
      ]
    },
    { 
      label: 'News', 
      url: '/news',
      submenu: [
        { label: 'Press Release / News', url: '/press-release' },
        { label: 'In the News', url: '/in-the-news' },
        { label: 'Spotlight Series', url: '/spotlight-series' }
      ]
    },
    { label: 'Contact Us', url: '/contact-us' }
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-[36px] left-0 right-0 z-50 bg-white transition-shadow duration-300 ${
        scrolled ? 'shadow-md' : 'shadow-none'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="header-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_secure-vote-homepage/artifacts/fv4hr1qo_LOGOsecurethevote_yellowMD.png" 
              alt="Secure the Vote Maryland"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center" data-testid="desktop-nav" aria-label="Main navigation">
            {navItems.map((item, index) => (
              <div 
                key={index} 
                className="relative"
                onMouseEnter={() => item.submenu && setOpenDropdown(index)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                {item.submenu && !item.external ? (
                  <button
                    type="button"
                    className="flex items-center gap-0.5 px-2.5 py-2 text-[13px] font-medium text-slate-700 hover:text-[#9E1B32] transition-colors whitespace-nowrap cursor-default"
                    data-testid={`nav-link-${index}`}
                    aria-haspopup="menu"
                    aria-expanded={openDropdown === index}
                  >
                    {item.label}
                    <ChevronDown size={12} />
                  </button>
                ) : item.external ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-0.5 px-2.5 py-2 text-[13px] font-medium text-slate-700 hover:text-[#9E1B32] transition-colors whitespace-nowrap"
                    data-testid={`nav-link-${index}`}
                  >
                    {item.label}
                    {item.submenu && <ChevronDown size={12} />}
                  </a>
                ) : (
                  <Link
                    to={item.url}
                    className="flex items-center gap-0.5 px-2.5 py-2 text-[13px] font-medium text-slate-700 hover:text-[#9E1B32] transition-colors whitespace-nowrap"
                    data-testid={`nav-link-${index}`}
                  >
                    {item.label}
                    {item.submenu && <ChevronDown size={12} />}
                  </Link>
                )}
                
                {/* Dropdown */}
                {item.submenu && openDropdown === index && (
                  <div className="absolute top-full left-0 bg-white shadow-lg rounded-md py-2 min-w-[200px] z-50 border border-slate-100">
                    {item.submenu.map((subItem, subIndex) => (
                      subItem.external ? (
                        <a
                          key={subIndex}
                          href={subItem.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#9E1B32]"
                        >
                          {subItem.label}
                        </a>
                      ) : (
                        <Link
                          key={subIndex}
                          to={subItem.url}
                          className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#9E1B32]"
                        >
                          {subItem.label}
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            <Button
              asChild
              className="ml-3 bg-[#9E1B32] hover:bg-[#7a1526] text-white px-5 h-9 rounded-sm font-semibold text-[13px]"
              data-testid="header-donate-btn"
            >
              <Link to={config.cta_url || '/donate'}>DONATE NOW</Link>
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setMobileDropdown(null); }}
            aria-label={mobileMenuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? (
              <X className="text-slate-900" size={24} />
            ) : (
              <Menu className="text-slate-900" size={24} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden bg-white shadow-lg border-t p-4 max-h-[calc(100vh-92px)] overflow-y-auto overscroll-contain"
            data-testid="mobile-menu"
            id="mobile-navigation"
          >
            <nav className="flex flex-col gap-2 pb-2">
              {navItems.map((item, index) => (
                <div key={index}>
                  {item.submenu && !item.external ? (
                    <button
                      type="button"
                      className="flex items-center justify-between py-2 text-slate-700 font-medium hover:text-[#9E1B32] text-left w-full"
                      onClick={() => setMobileDropdown(mobileDropdown === index ? null : index)}
                      aria-expanded={mobileDropdown === index}
                      aria-controls={`mobile-submenu-${index}`}
                    >
                      {item.label}
                      <ChevronDown size={16} className={mobileDropdown === index ? "rotate-180" : ""} />
                    </button>
                  ) : item.external ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block py-2 text-slate-700 font-medium hover:text-[#9E1B32]"
                      onClick={() => !item.submenu && setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.url}
                      className="block py-2 text-slate-700 font-medium hover:text-[#9E1B32]"
                      onClick={() => !item.submenu && setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                  {item.submenu && mobileDropdown === index && (
                    <div id={`mobile-submenu-${index}`} className="pl-4 border-l-2 border-slate-200 ml-2">
                      {item.url === "/news" && (
                        <Link to="/news" className="block py-1.5 text-sm text-slate-600 hover:text-[#9E1B32]" onClick={() => setMobileMenuOpen(false)}>All News</Link>
                      )}
                      {item.submenu.map((subItem, subIndex) => (
                        subItem.external ? (
                        <a
                          key={subIndex}
                          href={subItem.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block py-1.5 text-sm text-slate-600 hover:text-[#9E1B32]"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {subItem.label}
                        </a>
                      ) : (
                        <Link
                          key={subIndex}
                          to={subItem.url}
                          className="block py-1.5 text-sm text-slate-600 hover:text-[#9E1B32]"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {subItem.label}
                        </Link>
                      )
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Button
                asChild
                className="bg-[#9E1B32] hover:bg-[#7a1526] text-white w-full mt-2"
              >
                <Link to="/donate" onClick={() => setMobileMenuOpen(false)}>
                  DONATE NOW
                </Link>
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
